/** @jsx jsx */
import React from 'react';
import { css, jsx } from '@compiled/react';
import Badge from '@atlaskit/badge';
import Heading from '@atlaskit/heading';
import { N40 } from '@atlaskit/theme/colors';
import { gridSize as getGridSize } from '@atlaskit/theme/constants';
import { token } from '@atlaskit/tokens';
import { useIntl } from '@atlassian/jira-intl';
import {
	type TimeTrackingOptions,
	timeTrackingFormatter,
} from '@atlassian/jira-time-tracking-formatter';
import { useFormatStoryPointsTotal } from '../../../../../../common/hooks/use-format-story-points-total';
import type { Assignee } from '../../../types';
import messages from './messages';

const gridSize = getGridSize();

type Props = {
	total: Assignee;
	isStoryPointsEnabled: boolean;
	isOriginalEstimationEnabled: boolean;
	isRemainingTimeEstimationEnabled: boolean;
	timeTrackingOptions: TimeTrackingOptions;
	includeSubtasks: boolean;
};

const SummaryTableFooter = (props: Props) => {
	const {
		total,
		isStoryPointsEnabled,
		isOriginalEstimationEnabled,
		isRemainingTimeEstimationEnabled,
		timeTrackingOptions,
		includeSubtasks,
	} = props;

	const intl = useIntl();
	const { formatMessage } = intl;
	const formatStoryPointsTotal = useFormatStoryPointsTotal();

	const originalTimeEstimate = timeTrackingFormatter(
		total.originalTimeEstimateInSeconds,
		timeTrackingOptions,
		intl,
	);

	const remainingTimeEstimate = timeTrackingFormatter(
		total.remainingTimeEstimateInSeconds,
		timeTrackingOptions,
		intl,
	);

	return (
		<tfoot>
			<tr>
				<td css={bottomBorderStyles}>
					<Heading level="h200" as="h3">
						{formatMessage(messages.totalSummaryLabel)}
					</Heading>
				</td>
				<td css={bottomBorderStyles}>{total.issueCount}</td>
				{includeSubtasks && <td css={bottomBorderStyles}>{total.subtaskCount}</td>}
				{isStoryPointsEnabled && (
					<td css={bottomBorderStyles}>
						<Badge max={1000}>{formatStoryPointsTotal(total.storyPoints)}</Badge>
					</td>
				)}
				{isOriginalEstimationEnabled && (
					<td css={bottomBorderStyles}>
						<Badge>{originalTimeEstimate}</Badge>
					</td>
				)}
				{isRemainingTimeEstimationEnabled && (
					<td css={bottomBorderStyles}>
						<Badge>{remainingTimeEstimate}</Badge>
					</td>
				)}
			</tr>
		</tfoot>
	);
};

export default SummaryTableFooter;

const bottomBorderStyles = css({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	borderBottom: `${gridSize / 4}px solid ${token('color.border', N40)}`,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	borderTop: `${gridSize / 4}px solid ${token('color.border', N40)}`,
	paddingTop: token('space.100', '8px'),
	paddingBottom: token('space.100', '8px'),
});
